/**
 * STATUS BADGE
 */
export const STATUS_BADGE = {
  all     : { color: "", title: "All" },
  active  : { color: "success", title: "Active" },
  disabled: { color: "danger", title: "Disabled" }
};

export const KYC_TONE = {
  not_started       : "neutral",
  pending           : "warning",
  submitted         : "info",
  verifying         : "warning",
  verified          : "success",
  failed            : "destructive",
  escalated         : "destructive",
  in_progress       : "warning",
  documents_received: "info",
  rejected          : "destructive"
};

export const KYC_LABEL = {
  not_started       : "Not started",
  pending           : "Pending",
  submitted         : "Submitted",
  verifying         : "Verifying",
  verified          : "Verified",
  failed            : "Failed",
  escalated         : "Escalated",
  in_progress       : "In progress",
  documents_received: "Documents received",
  rejected          : "Rejected"
};
export const PAYOUT_TONE = { pending: "neutral", eligible: "info", paid: "success", held: "held", not_eligible: "destructive", reverted: "destructive", awaiting_kyc: "warning" };
export const PAYOUT_LABEL = { pending: "Pending", eligible: "Eligible", paid: "Paid", held: "Held", not_eligible: "Not eligible", reverted: "Reverted", awaiting_kyc: "Awaiting KYC" };

export const PAYOUT_STAGE_TONE = { awaiting_kyc: "neutral", eligible: "info", paid: "success", held: "held" };
export const PAYOUT_STAGE_LABEL = { awaiting_kyc: "Awaiting KYC", eligible: "Eligible", paid: "Paid", held: "Held" };
export const KYC_STATUS_OPTIONS = [
    { value: "not_started", label: "Not started" },
    { value: "in_progress", label: "In progress" },
    { value: "documents_received", label: "Documents received" },
    { value: "verified", label: "Verified" },
    { value: "rejected", label: "Rejected" }
];

export const PAYOUT_STATUS_OPTIONS = [
    { value: "eligible", label: "Eligible" },
    { value: "not_eligible", label: "Not Eligible" }
];
export const INVOICE_STATUS_TONE = { pending: "neutral", eligible: "info", paid: "success", held: "held", not_eligible: "destructive", reverted: "destructive" };
export const INVOICE_STATUS_LABEL = { pending: "Pending", eligible: "Eligible", paid: "Paid", held: "Held", not_eligible: "Not eligible", reverted: "Reverted" };

// How often Nook Pay raises a debit note against a brand. Mirrors the API's
// DebitNoteFrequency enum — the values are what goes over the wire.
export const DEBIT_NOTE_FREQUENCY = {
    MONTHLY          : "monthly",
    FORTNIGHTLY      : "fortnightly",
    PER_INVOICE_CYCLE: "per_invoice_cycle"
};

export const FREQUENCY_LABEL = {
    [DEBIT_NOTE_FREQUENCY.MONTHLY]          : "Monthly",
    [DEBIT_NOTE_FREQUENCY.FORTNIGHTLY]      : "Fortnightly",
    [DEBIT_NOTE_FREQUENCY.PER_INVOICE_CYCLE]: "Per invoice cycle"
};

export const DEBIT_NOTE_STATUS_TONE = {
    draft        : "neutral",
    issued       : "warning",
    acknowledged : "info",
    approved     : "info",
    fund_received: "held",
    in_progress  : "info",
    rejected     : "destructive",
    settled      : "success",
    reverted     : "destructive"
};
export const DEBIT_NOTE_STATUS_LABEL = {
    draft        : "Draft",
    issued       : "Issued",
    acknowledged : "Acknowledged",
    approved     : "Approved",
    fund_received: "Fund received",
    in_progress  : "In progress",
    rejected     : "Rejected",
    settled      : "Settled",
    reverted     : "Reverted"
};

export const DEBIT_NOTE_STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "issued", label: "Issued" },
    { value: "acknowledged", label: "Acknowledged" },
    { value: "approved", label: "Approved" },
    { value: "fund_received", label: "Fund received" },
    { value: "in_progress", label: "In progress" },
    { value: "settled", label: "Settled" },
    { value: "rejected", label: "Rejected" },
    { value: "reverted", label: "Reverted" }
];

// The only statuses the ops disbursement queue returns (and accepts as `status`) —
// omitting the filter returns all three.
export const DISBURSEMENT_STATUS_OPTIONS = [
    { value: "fund_received", label: "Fund received" },
    { value: "in_progress", label: "In progress" },
    { value: "settled", label: "Settled" }
];

// How a brand pays its publishers. Mirrors the API's PaymentModel enum.
// `not_selected` is the state before onboarding answers the question — such a brand
// belongs to neither book, so Ops can open it under either payment model.
export const PAYMENT_MODEL = {
    MANAGED_PAYOUT: "managed_payout",
    SELF_PAYOUT   : "self_payout",
    NOT_SELECTED  : "not_selected"
};

// Carries the retired values (custodian, agency, self_pay) too, so rows saved
// before the enum settled still render a label instead of a raw string.
export const PAYMENT_MODEL_LABEL = {
    [PAYMENT_MODEL.MANAGED_PAYOUT]: "Managed payouts",
    [PAYMENT_MODEL.SELF_PAYOUT]   : "Self payout",
    [PAYMENT_MODEL.NOT_SELECTED]  : "Not selected",
    custodian                     : "Custodian",
    agency                        : "Agency",
    self_pay                      : "Self-Pay"
};

export const COUNTRY_LABEL = {
    IN: "India",
    US: "United States",
    GB: "United Kingdom",
    SG: "Singapore",
    AE: "United Arab Emirates",
    AU: "Australia",
    CA: "Canada"
};

export const PAYMENT_MODEL_OPTIONS = [
    { value: PAYMENT_MODEL.MANAGED_PAYOUT, label: PAYMENT_MODEL_LABEL[PAYMENT_MODEL.MANAGED_PAYOUT] },
    { value: PAYMENT_MODEL.SELF_PAYOUT, label: PAYMENT_MODEL_LABEL[PAYMENT_MODEL.SELF_PAYOUT] },
    { value: "agency", label: "Agency (Future scope)", disabled: true }
];

export const FREQUENCY_OPTIONS = Object.values(DEBIT_NOTE_FREQUENCY).map((value) => ({
    value,
    label: FREQUENCY_LABEL[value]
}));

// Drives the brand workspace gate: a brand whose payment setup is still pending
// is locked behind /brand/:brandId/setup (see helpers/brand-setup).
export const PAYMENT_SETUP_STATUS = {
    PENDING  : "pending",
    COMPLETED: "completed"
};

export const PAYMENT_SETUP_STATUS_TONE = {
    [PAYMENT_SETUP_STATUS.PENDING]  : "warning",
    [PAYMENT_SETUP_STATUS.COMPLETED]: "success"
};
export const PAYMENT_SETUP_STATUS_LABEL = {
    [PAYMENT_SETUP_STATUS.PENDING]  : "Pending",
    [PAYMENT_SETUP_STATUS.COMPLETED]: "Completed"
};

export const PAYMENT_SETUP_STATUS_OPTIONS = Object.values(PAYMENT_SETUP_STATUS).map((value) => ({
    value,
    label: PAYMENT_SETUP_STATUS_LABEL[value]
}));

export const BRAND_STATUS_TONE = { active: "success", disabled: "destructive" };
export const BRAND_STATUS_LABEL = { active: "Active", disabled: "Disabled" };

export const PLATFORM_OPTIONS = [
    { value: "trackier", label: "Trackier" },
    { value: "apptrove", label: "Apptrove", disabled: true },
    { value: "affnook", label: "Affnook", disabled: true  }
];
export const PLATFORM_LABEL = { trackier: "Trackier", apptrove: "Apptrove", affnook: "Affnook" };

// Windows a brand can give its publishers to finish KYC before payouts are held.
// Days, kept as strings so they drop straight into a <select>.
export const KYC_WINDOW_OPTIONS = [
    { value: "15", label: "15 days" },
    { value: "30", label: "30 days" },
    { value: "45", label: "45 days" },
    { value: "60", label: "60 days" },
    { value: "90", label: "90 days" }
];

export const CURRENCY = "INR";